import { isMatch, merge, pick } from 'lodash';
import qs from 'qs';
import { useCallback, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';

import usePersistent from 'hooks/use-persistent';
import { ROUTES } from 'pages/Search/constants';
import { noneIf } from 'pages/Search/utils';

interface Payload {
    page: number;
    size: number;
    year?: number;
    month?: number;
    sort?: {
        field: 'vendor' | 'product' | 'cves';
        direction: 'asc' | 'desc';
    };
}

const defaults: Payload = {
    page: 1,
    size: 20,
    year: undefined,
    month: undefined,
    sort: {
        field: 'cves',
        direction: 'desc'
    }
};

export function useYearMonthProductCatalogPayload() {
    const history = useHistory();
    const location = useLocation();
    const parameters = useParams<{ year: string; month: string; page: string }>();
    const query = useMemo(() => qs.parse(location.search, { ignoreQueryPrefix: true }), [location.search]);

    const page = parameters.page ? parseInt(parameters.page) : defaults.page;
    const year = parameters.year ? parseInt(parameters.year) : defaults.year;
    const month = parameters.month ? parseInt(parameters.month) : defaults.month;
    const size = query.size ? parseInt(query.size.toString()) : defaults.size;
    const sort = query.sort || defaults.sort;

    const payload = useMemo(() => ({ page, size, year, month, sort } as Payload), [month, page, size, sort, year]);
    const payloadRef = usePersistent(payload);

    const url = useCallback(
        (patch: Partial<Payload>) => {
            const previous = payloadRef.current;
            const current = merge({}, previous, patch);

            return ROUTES.YEARS({
                year: current.year,
                month: current.year ? current.month : undefined,
                page: isMatch(previous, pick(current, ['year', 'month', 'size']))
                    ? noneIf(current.page, defaults.page)
                    : undefined,
                size: noneIf(current.size, defaults.size),
                sort: noneIf(current.sort, defaults.sort)
            });
        },
        [payloadRef]
    );

    const set = useCallback((patch: Partial<Payload>) => history.push(url(patch)), [history, url]);

    const actions = useMemo(() => ({ url, set }), [set, url]);

    return [payload, actions] as const;
}
